import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmPopup from './ConfirmPopup';
import { toast } from 'react-toastify';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';

interface BranchItem {
    id: number;
    name: string;
    scheduleBranchId: number;
}

interface TruckItem {
    id: number;
    scheduleBranchId: number;
    branchName: string;
    name: string;
    registrationNumber: string;
    defaultDriverId: number;
    defaultDriver: string;
    tiltCraneTruck: boolean;
}

interface DriverItem {
    id: number;
    name: string;
    scheduleBranchId: number;
}

const AdminTrucksData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [trucks, setTrucks] = React.useState<TruckItem[]>([]);
    const [companyBranchOptions, setCompanyBranchOptions] = React.useState<BranchItem[]>([]);
    const [companyDrivers, setCompanyDrivers] = React.useState<DriverItem[]>([]);
    const [companyBranchId, setCompanyBranchId] = React.useState(-1);
    const [scheduleBranchId, setScheduleBranchId] = React.useState(-1);
    const [truckName, setTruckName] = React.useState("");
    const [truckDefaultDriverId, setTruckDefaultDriverId] = React.useState(-1);
    const [truckRego, setTruckRego] = React.useState("");
    const [tiltCrane, setTiltCrane] = React.useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [deleteIndex, setDeleteIndex] = React.useState(-1);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("AdminCreateTrucks");
        if (hasPermission) {
            getCompanyBranches();
        } else {
            navigate("/accessdenied");
        }
    }

    const getCompanyBranches = async () => {
        const apiUrl = "GetCompanyBranchesDrivers";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setCompanyBranchOptions(result.data.branches);
            setCompanyDrivers(result.data.drivers);
            setSystemCompanyId(result.data.systemCompanyId);
            getTrucks();
        }
    }

    const getTrucks = async () => {
        const apiUrl = "GetTrucks";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setTrucks(result.data);
            setLoading(false);
        }
    }

    const clearError = (errorItem: string) => {
        let errorList = errors;
        delete errorList[errorItem];

        setErrors(errorList);
    }

    const handleBranchChange = (e:any) => {
        e.preventDefault();

        clearError(e.target.name);
        var branchId = e.target.value * 1;
        var schedCompId = -1;
        if (branchId > 0) {
            //find the branch item so I can set the schedule branch id in order to show the correct users for that branch
            var branch = companyBranchOptions.find(b => b.id == branchId);
            if (branch) {
                schedCompId = branch.scheduleBranchId;
                setScheduleBranchId(schedCompId);
            }
        } else {
            setScheduleBranchId(-1);
        }
        setCompanyBranchId(branchId);
    }

    const handleNameChange = (e: any) => {
        e.preventDefault();

        clearError(e.target.name);
        setTruckName(e.target.value);
    }

    const handleDriverChange = (e: any) => {
        clearError(e.target.name);
        setTruckDefaultDriverId(e.target.value * 1);
    }

    const handleRegoChange = (e: any) => {
        e.preventDefault();

        clearError(e.target.name);
        setTruckRego(e.target.value);
    }

    //DELETE TRUCK
    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        setShowDeleteConfirm(true);
        setDeleteIndex(itemIndex);
    }

    const hideDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setDeleteIndex(-1);
    }

    const saveDeleteConfirm = () => {
        saveDeleteTruck();
    }

    const saveDeleteTruck = async () => {
        var truckList = [...trucks];
        var truckToDelete = trucks[deleteIndex];

        var deleteTruck = {
            id: truckToDelete.id
        };

        var jsonItem = JSON.stringify(deleteTruck);

        const apiUrl = "DeleteTruck";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Truck Deleted Successfully");
                truckList.splice(deleteIndex, 1);
                setShowDeleteConfirm(false);
                setDeleteIndex(-1);
                setTrucks(truckList);
            }
        }
    }
    //END DELETE TRUCK

    const onDefaultDriverCellChange = (e: any, jobIndex: number) => {
        e.preventDefault();
        var truckList = [...trucks];
        var truckToEdit = trucks[jobIndex];
        truckToEdit.defaultDriverId = e.target.value * 1;
        setTrucks(truckList);
        saveDefaultDriver(truckToEdit);
    }

    const saveDefaultDriver = async (editTruck: TruckItem) => {
        var saveTruck = {
            id: editTruck.id,
            defaultDriverId: editTruck.defaultDriverId
        };

        var jsonItem = JSON.stringify(saveTruck);

        const apiUrl = "UpdateTruck";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Truck Saved");
            }
        }
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (companyBranchId <= 1) {
            errorList["companyBranchId"] = "Branch is required!";
            formIsValid = false;
        }
        
        if (!truckName) {
            errorList["truckName"] = "Truck Name is required!";
            formIsValid = false;
        }
        
        setErrors(errorList)
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            saveTruck();
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const saveTruck = async () => {
        var saveNewTruck = {
            companyBranchId: companyBranchId,
            truckName: truckName,
            truckRegistrationNumber: truckRego,
            truckDefaultDriverId: truckDefaultDriverId,
            tiltCrane: tiltCrane
        };

        var jsonItem = JSON.stringify(saveNewTruck);

        const apiUrl = "SaveTruck";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Truck Saved Successfully");
                setLoading(true);
                setCompanyBranchId(-1);
                setScheduleBranchId(-1);
                setTruckDefaultDriverId(-1);
                setTruckName("");
                setTruckRego("");
                setTiltCrane(false);
                getTrucks();
            }
        }
    }

    const confirmPopup = <ConfirmPopup heading="Delete Truck?" text="Are you sure you want to delete this truck?" hideConfirmModal={hideDeleteConfirm} showConfirmModal={showDeleteConfirm} saveConfirmModal={saveDeleteConfirm} />
    const companyDriversFiltered = companyDrivers.filter(c => c.scheduleBranchId == scheduleBranchId);

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
            </div>
            <div>Note: If you want to edit a truck, you need to delete existing and then add a new one (you can add a new one with the same name if you have deleted it)</div>
            <div>
                <label className="input-group" htmlFor="companyBranchId">
                    <span className="label">Branch</span>
                    <select className="select" id="companyBranchId" name="companyBranchId" value={companyBranchId} onChange={(e) => handleBranchChange(e)}>
                        <option hidden defaultValue="-1" value={-1}></option>
                        {companyBranchOptions.map(branch =>
                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["companyBranchId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["companyBranchId"]}</span>
                
                <label className="input-group" htmlFor="truckName">
                    <span className="label">Name</span>
                    <input type='text' id="truckName" name="truckName" className='input' value={truckName} onChange={(e) => handleNameChange(e)}></input>
                </label>
                <span className={errors["truckName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["truckName"]}</span>

                <label className={systemCompanyId === 3 ? "input-group" : "hidden"} htmlFor="truckDefaultDriverId">
                    <span className="label">Default Driver</span>
                    <select className="select" id="truckDefaultDriverId" name="truckDefaultDriverId" value={truckDefaultDriverId} onChange={(e) => handleDriverChange(e)}>
                        <option hidden defaultValue="-1" value={-1}></option>
                        {companyDriversFiltered.map(driver =>
                            <option key={driver.id} value={driver.id}>{driver.name}</option>
                        )};
                    </select>
                </label>

                <label className="input-group" htmlFor="truckRego">
                    <span className="label">Rego Number</span>
                    <input type='text' id="truckRego" name="truckRego" className='input' value={truckRego} onChange={(e) => handleRegoChange(e)}></input>
                </label>
                <label className="input-group" htmlFor="tiltCrane">
                    <span className="label">Tilt/Crane Truck?</span>
                    <input className="checkbox" type="checkbox" id="tiltCrane" name="tiltCrane" checked={tiltCrane} onChange={() => setTiltCrane(!tiltCrane)}></input>
                </label>
                <button className="defaultbutton marginLeftLabel" onClick={save} type="button">Add Truck</button>
            </div>
            <hr></hr>
            <div className="overflowAuto">
                <table className="table tableColours pricelist-width">
                    <thead>
                        <tr>
                            <th className="hidden">Id</th>
                            <th>Branch</th>
                            <th>Name</th>
                            <th className={systemCompanyId === 3 ? "" : "hidden"}>Default Driver</th>
                            <th>Rego</th>
                            <th className="textaligncenter">Tilt/Crane?</th>
                            <th></th>
                        </tr>
                    </thead>
                    {trucks.map((item, index) =>
                        <tbody key={index}>
                            <tr>
                                <td className="hidden">{item.id}</td>
                                <td className="table__text--align">{item.branchName}</td>
                                <td className="table__text--align">{item.name}</td>
                                <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                    <select className="select" id="truckId" name="truckId" value={item.defaultDriverId} onChange={(e) => onDefaultDriverCellChange(e, index)} >
                                        <option defaultValue="-1" value={-1}></option>
                                        {companyDrivers.filter(c => c.scheduleBranchId == item.scheduleBranchId).map(driver =>
                                            <option key={driver.id} value={driver.id}>{driver.name}</option>
                                        )};
                                    </select>
                                </td>
                                <td className="table__text--align">{item.registrationNumber}</td>
                                <td className="table__text--align textaligncenter">
                                    <input type="checkbox" name="tiltCraneTruck" checked={item.tiltCraneTruck} disabled></input>
                                </td>
                                <td className="table__text--align">
                                    <div className="delete--tablecell">
                                        <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                            <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            <h1>Trucks</h1>
            {contents}
        </div>
    );
}

export default AdminTrucksData;